// @flow strict

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import moment from 'moment';
import type {InterviewFeedbackProps} from 'src/types/crm.js';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardActions,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  ButtonTextMedium,
  SubTitleExtraSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import SubmitFeedbacks from 'src/components/crm/jobs/job-info/overview/feedbacks/submit-feedbacks.jsx';
import {
  // $FlowFixMe[untyped-import]
  formatMomentDateWithFormat,
  // $FlowFixMe[untyped-import]
  formatMomentTimeWithFormat,
} from 'src/utils/date-time.js';
import classify from 'src/utils/classify';
import css from './interview-feedback.css';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {useHistory, useParams} from 'src/rerouter';


const InterviewFeedback = ({
  feedbackData,
  from = 'DASHBOARD',
}: InterviewFeedbackProps): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const history = useHistory();
  const {
    isReviewPanelOpen,
    stepCandidateId,
    candidateId,
    jobId: queryJobId,
    feedbackTabId,
    type,
  } = history.location.query;
  const {jobId: paramJobId} = useParams();
  const jobId = from === 'DASHBOARD' ? queryJobId : paramJobId;
  const interviewDate = formatMomentDateWithFormat(feedbackData.startDate);
  const time = `${formatMomentTimeWithFormat(
    feedbackData.startDate,
  )} - ${formatMomentTimeWithFormat(feedbackData.endDate)} ${t('IST', 'IST')}`;
  const pendingSince = moment(feedbackData.endDate).fromNow();
  const isOverdue = React.useMemo(() => {
    const today = moment().startOf('day');
    const interview = moment(feedbackData.endDate).startOf('day');
    return today.diff(interview, 'days') >= 1;
  }, [feedbackData.endDate]);
  const interviewDetails = [
    {
      text: `${t('Candidate', 'Candidate')}: ${feedbackData.candidateName}`,
      icon: 'user',
    },
    {
      text: `${t('Job', 'Job')}: ${feedbackData.jobTitle}`,
      icon: 'briefcase',
    },
    {
      text: `${t('Step', 'Step')}: ${feedbackData.stepName}`,
      icon: 'compass',
    },
  ];
  const handleSubmitFeedback = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('stepCandidateId', feedbackData.stepCandidateId);
    params.set('candidateId', feedbackData.candidateId.toString());
    if (from === 'DASHBOARD') {
      params.set('jobId', feedbackData.jobId);
    }
    params.set('feedbackTabId', 'interviewFeedbacks');
    params.set('type', 'interview');
    params.set('isReviewPanelOpen', 'true');
    params.set('candidateName', feedbackData.candidateName);
    const rerouteUrl =
      from === 'DASHBOARD' ? '/dashboard' : `/crm/job/${jobId}/info/overview`;
    history.push(`${rerouteUrl}?${params.toString()}`);
  };

  return (
    <>
      {isReviewPanelOpen === 'true' && (
        <SubmitFeedbacks
          isOpen={isReviewPanelOpen === 'true'}
          handleClose={() => {
            if (from === 'DASHBOARD') {
              history.push(`/dashboard`);
            } else if (from === 'JOB_OVERVIEW' && jobId) {
              history.push(`/crm/job/${jobId}/info/overview`);
            }
          }}
          candidateStepId={stepCandidateId}
          candidateId={Number(candidateId)}
          jobId={jobId}
          tabId={feedbackTabId}
          type={type}
          from={from}
        />
      )}
      <Card
        classNames={{
          wrapper: css.innerCard,
        }}
      >
        <div className={css.cardHeader}>
          <Tooltip
            elevation="modal"
            title={`${t('Pending_since', 'Pending since')} ${pendingSince}`}
          >
            <ButtonTextMedium className={css.candidateName}>
              {feedbackData.subject}
            </ButtonTextMedium>
          </Tooltip>
          <CardActions>
            <Button
              onClick={handleSubmitFeedback}
              children={t('Submit_Feedback', 'Submit Feedback')}
              type="tertiary"
              size="small"
              iconLeftName="file-lines"
            />
          </CardActions>
        </div>

        <CardContent className={css.cardContent}>
          <Card
            classNames={{
              wrapper: css.innerInterviewFeedbackCard,
            }}
            paddingTop="small"
            paddingBottom="small"
          >
            <div className={css.innerCardContent}>
              <div className={css.dateContainer}>
                <div className={css.item}>
                  <Icon
                    color={isOverdue ? 'danger' : 'primary'}
                    name="calendar"
                    size="small"
                  />
                  <SubTitleExtraSmall
                    className={classify({
                      [css.overdueDate]: isOverdue,
                    })}
                  >
                    {interviewDate}
                  </SubTitleExtraSmall>
                </div>
                {isOverdue && (
                  <Chip semantic="danger" size="small">
                    {t('Overdue', 'Overdue')}
                  </Chip>
                )}
              </div>
              <div className={css.item}>
                <Icon name="clock" size="small" />
                <SubTitleExtraSmall>{time}</SubTitleExtraSmall>
              </div>
            </div>
            <div className={css.innerCardContent}>
              {interviewDetails.map(({text, icon}) => (
                <div className={css.item}>
                  <Icon name={icon} size="small" />
                  <SubTitleExtraSmall>{text}</SubTitleExtraSmall>
                </div>
              ))}
            </div>
          </Card>
        </CardContent>
      </Card>
    </>
  );
};

export default InterviewFeedback;
